import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import AuthLayout from "components/AuthLayout/AuthLayout";
import { authPageMeta } from "data/authPages.enum";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { IVerifyEmail } from "Interfaces/interface.auth";
import { resendVerificationCode, verifyOtp } from "store/slices/authSlice";
import { useAppDispatch } from "store/hooks/hooks";
import { ClipLoader } from "react-spinners";

const VerifyEmail: React.FC<IVerifyEmail> = ({
  email,
  onSuccess,
  switchToLogin,
  switchToSignUp,
  setSubmittingForm,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const [verifying, setVerifying] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const dispatch = useAppDispatch();

  if (!email) {
    toast.error("Missing email. Redirecting to signup.");
    switchToSignUp();
    return null;
  }

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      setOtp(newOtp);
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter a valid 6-digit code.");
      return;
    }
    setVerifying(true);
    setSubmittingForm(true);
    try {
      await dispatch(
        verifyOtp({
          type: "email",
          value: email,
          code,
        })
      ).unwrap();
      onSuccess();
      setVerifying(false);
    } catch (error: any) {
      setVerifying(false);
    } finally {
      setVerifying(false);
      setSubmittingForm(false);
    }
  };

  const handleResendCode = async () => {
    setIsResendingOtp(true);
    try {
      await dispatch(
        resendVerificationCode({
          type: "email",
          value: email,
        })
      ).unwrap();
      setIsResendingOtp(false);
    } catch (error: any) {
      setIsResendingOtp(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-white dark:bg-neutral-900 rounded-2xl p-8 dark:border-neutral-700"
      >
        <p className="text-sm text-center text-neutral-600 dark:text-neutral-400 mb-6">
          We've sent a 6-digit code to{" "}
          <span className="font-medium text-neutral-900 dark:text-white">
            {email}
          </span>
        </p>

        <div className="flex justify-center gap-3 mb-6">
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              disabled={verifying || isResendingOtp}
              ref={(el) => (inputsRef.current[index] = el)}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-14 rounded-xl text-xl text-center border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition disabled:cursor-not-allowed disabled:opacity-50"
            />
          ))}
        </div>

        <ButtonPrimary
          onClick={handleVerify}
          disabled={verifying || isResendingOtp}
          loading={verifying}
          className="w-full"
        >
          {!verifying && "Verify Email"}
        </ButtonPrimary>

        <div className="text-center mt-4 font-inter text-sm">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            Didn’t receive a code?{" "}
            <button
              className="text-primary-500 dark:text-primary-400 font-medium hover:underline disabled:pointer-events-none disabled:opacity-50"
              onClick={handleResendCode}
              disabled={verifying || isResendingOtp}
            >
              {isResendingOtp ? (
                <ClipLoader
                  loading={true}
                  size={14}
                  color="blue"
                  className="mx-4"
                />
              ) : (
                "Resend"
              )}
            </button>
          </span>
        </div>

        <div className="text-center mt-4">
          <button
            onClick={switchToLogin}
            disabled={verifying || isResendingOtp}
            className="text-primary-500 hover:text-primary-600 font-medium hover:underline transition-colors duration-300 font-inter text-sm disabled:pointer-events-none disabled:opacity-50"
          >
            Back to sign In
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default VerifyEmail;
